import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { SiWhatsapp } from "react-icons/si";
import { Loader2, CheckCircle2, Clock, Camera, Info, ExternalLink, FileUp, RotateCcw } from "lucide-react";
import { makeForm, portalChunks } from "@/lib/upload";
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { T } from "@/components/PortalBulk";
import { sendTitle, sendWhy, STATE_TXT } from "@/components/portal/copy";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const CC_KEYS = ["id_representante_vendedor", "id_vendedor", "id_comprador"];
export const isCard = (k) => CC_KEYS.includes(k) || /^cc_(conjuge|herdeiros|cabeca_casal)/.test(k);
const ownerOnly = (k) => isCard(k) || /^(caderneta_predial|habilitacao_herdeiros|procuracao)/.test(k);
export const DARK = { backgroundColor: "var(--btn)", color: "#ffffff" };
export const waDigits = (ph) => { const d = (ph || "").replace(/\D/g, ""); return d.length === 9 ? `351${d}` : d; };
const errOf = (ex, fb) => (typeof ex.response?.data?.detail === "string" ? ex.response.data.detail : fb);

function parseHelp(text) {
  const steps = [], notes = [];
  (text || "").split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean).forEach((p) => {
    const lines = p.split("\n").map((l) => l.trim());
    const st = lines.filter((l) => /^\d+\.\s/.test(l));
    if (!st.length) { notes.push(p); return; }
    const head = lines.filter((l) => !/^\d+\.\s/.test(l)).join(" ").trim();
    if (head && !/^como obter:?$/i.test(head)) notes.push(head.replace(/:$/, ""));
    st.forEach((l) => steps.push(l.replace(/^\d+\.\s*/, "")));
  });
  return { steps, notes };
}

function MetaBoxes({ meta }) {
  const boxes = [["Custo", meta?.cost], ["Prazo", meta?.time], ["Onde", meta?.where]].filter(([, v]) => v);
  if (!boxes.length) return null;
  return (
    <div className="grid grid-cols-3 gap-2 mt-4" data-testid="portal-help-meta">
      {boxes.map(([k, v]) => (
        <div key={k} className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2.5">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{k}</div>
          <div className="text-sm font-semibold brand-text mt-0.5 leading-snug">{v}</div>
        </div>
      ))}
    </div>
  );
}

export function HelpSheet({ doc, consultant, onClose, onHandOver }) {
  const { steps, notes } = parseHelp(doc?.help);
  const meta = doc?.meta;
  const phone = waDigits(consultant?.phone);
  const waHelp = doc && phone && `https://wa.me/${phone}?text=${encodeURIComponent(`Olá ${consultant.name}, não consigo obter: ${doc.client_name}. Pode ajudar-me?`)}`;
  return (
    <Drawer open={!!doc} onOpenChange={(o) => { if (!o) onClose(); }} shouldScaleBackground={false}>
      <DrawerContent className="max-h-[88vh] bg-white" data-testid="portal-help-sheet">
        {doc && (
          <div className="overflow-y-auto px-5 pt-3 pb-8 max-w-xl mx-auto w-full">
            <DrawerTitle className="font-heading text-xl brand-text">{doc.noun ? `Como pedir a ${doc.noun}` : doc.client_name}</DrawerTitle>
            <DrawerDescription className="sr-only">Como obter este documento</DrawerDescription>
            <MetaBoxes meta={meta} />
            {notes[0] && <p className="text-sm text-slate-600 mt-4 leading-relaxed">{notes[0]}</p>}
            {steps.length > 0 && (
              <ol className="mt-4 space-y-3.5" data-testid="portal-help-steps">
                {steps.map((s, i) => (
                  <li key={i} className="flex gap-3 text-[15px] text-slate-700 leading-snug">
                    <span className="w-6 h-6 rounded-full text-white text-xs font-bold grid place-items-center shrink-0" style={DARK}>{i + 1}</span>
                    <span className="pt-0.5">{s}</span>
                  </li>
                ))}
              </ol>
            )}
            {notes.slice(1).map((n, i) => <p key={i} className="text-sm text-slate-600 mt-4 leading-relaxed whitespace-pre-line">{n}</p>)}
            {!doc.help && <p className="text-sm text-slate-600 mt-4">Sem instruções disponíveis. Em caso de dúvida, fale com o seu consultor.</p>}
            {meta?.url && (
              <a href={meta.url} target="_blank" rel="noreferrer" data-testid="portal-help-site"
                className="mt-6 w-full h-11 inline-flex items-center justify-center gap-2 rounded-lg text-white font-semibold text-sm" style={DARK}>
                Abrir {new URL(meta.url).hostname.replace(/^www\./, "")} <ExternalLink size={15} />
              </a>
            )}
            {waHelp && (
              <a href={waHelp} target="_blank" rel="noreferrer" data-testid="portal-help-consultant"
                className="mt-2 w-full h-11 inline-flex items-center justify-center gap-2 rounded-lg border-2 border-slate-200 font-semibold text-sm brand-text">
                <SiWhatsapp size={15} className="text-[#25D366]" /> Não consigo, peço ajuda a {consultant.name}
              </a>
            )}
            {onHandOver && !doc.consultant_handles && !ownerOnly(doc.key) && (
              <button onClick={() => onHandOver(doc.key)} data-testid="portal-help-handover"
                className="mt-2 w-full h-11 inline-flex items-center justify-center rounded-lg border-2 border-slate-200 font-semibold text-sm brand-text hover:bg-slate-50">
                Prefiro que o consultor trate
              </button>
            )}
            {doc.consultant_handles && <p className="mt-3 text-center text-sm text-slate-600" data-testid="portal-help-handed">O consultor vai tratar deste documento.</p>}
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
}

function TitularCard({ token, session, doc, onDone }) {
  const [busy, setBusy] = useState("");
  const act = async (kind) => {
    setBusy(kind);
    try {
      await axios.post(`${API}/portal/${token}/documents/${doc.key}/${kind}`, kind === "owner-claim" ? { name: doc.owner_name } : {}, { headers: { "X-Portal-Session": session } });
      onDone();
    } catch (ex) { toast.error(errOf(ex, "Não foi possível guardar. Tente novamente.")); }
    finally { setBusy(""); }
  };
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-4" data-testid={`portal-doc-${doc.key}`}>
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0 font-semibold brand-text">{doc.client_name}</div>
        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 shrink-0" data-testid={`portal-doc-badge-${doc.key}`}>A confirmar</span>
      </div>
      <div className="mt-3 rounded-lg bg-amber-50 text-amber-900 text-[15px] leading-snug px-3.5 py-3" data-testid={`portal-doc-titular-${doc.key}`}>
        {`${doc.noun === "caderneta" ? "Esta caderneta" : "Esta certidão"} está em nome de `}<b>{doc.owner_name}</b>, que ainda não está indicado como proprietário.
      </div>
      <button onClick={() => act("owner-claim")} disabled={!!busy} data-testid={`portal-doc-owner-claim-${doc.key}`}
        className="mt-3 w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl text-white font-semibold disabled:opacity-60" style={DARK}>
        {busy === "owner-claim" && <Loader2 size={16} className="animate-spin" />} É também dono do imóvel
      </button>
      <button onClick={() => act("wrong")} disabled={!!busy} data-testid={`portal-doc-wrong-${doc.key}`}
        className="mt-2 w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-slate-200 font-semibold brand-text disabled:opacity-60">
        {busy === "wrong" && <Loader2 size={16} className="animate-spin" />} Enviei a {doc.noun} errada
      </button>
    </div>
  );
}

// Enviado: em verificação pode ser trocado (apaga o antigo); verificado já não.
function SentCard({ token, session, doc, onDone }) {
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const ok = doc.state === "ok";
  const replace = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/portal/${token}/documents/${doc.key}/replace`, {}, { headers: { "X-Portal-Session": session } });
      toast.success("Ficheiro apagado. Pode enviar o certo.");
      onDone();
    } catch (ex) { toast.error(errOf(ex, "Não foi possível apagar. Tente novamente.")); setBusy(false); }
  };
  return (
    <div className={`rounded-xl border px-4 py-3 ${ok ? "border-green-100 bg-green-50 text-green-800" : "border-slate-200 bg-slate-50 text-slate-700"}`} data-testid={`portal-doc-${doc.key}`}>
      <div className="flex items-center gap-1.5 font-semibold">{ok ? <CheckCircle2 size={16} /> : <Clock size={16} className="text-slate-500" />} {doc.client_name}</div>
      <div className="text-sm opacity-75" data-testid={`portal-doc-state-${doc.key}`}>{ok ? STATE_TXT.ok : STATE_TXT.review}</div>
      {!ok && !ask && (
        <button type="button" onClick={() => setAsk(true)} className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold underline underline-offset-2 brand-text py-1" data-testid={`portal-doc-replace-${doc.key}`}>
          <RotateCcw size={14} /> Enviei o documento errado
        </button>
      )}
      {ask && (
        <div className="mt-3 rounded-lg bg-white border border-slate-200 p-3" data-testid={`portal-doc-replace-confirm-${doc.key}`}>
          <p className="text-[15px] text-slate-700 leading-snug">O ficheiro que enviou é apagado e pode enviar o certo a seguir.</p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setAsk(false)} disabled={busy} className="h-11 rounded-lg border-2 border-slate-200 font-semibold text-sm brand-text" data-testid={`portal-doc-replace-cancel-${doc.key}`}>Cancelar</button>
            <button type="button" onClick={replace} disabled={busy} className="h-11 inline-flex items-center justify-center gap-2 rounded-lg font-semibold text-sm text-white disabled:opacity-60" style={DARK} data-testid={`portal-doc-replace-yes-${doc.key}`}>
              {busy && <Loader2 size={16} className="animate-spin" />} Apagar e enviar outro
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ClaimedCard({ doc, consultant }) {
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-4" data-testid={`portal-doc-${doc.key}`}>
      <div className="flex items-center gap-2">
        <Clock size={18} className="text-slate-500 shrink-0" />
        <div className="flex-1 min-w-0 font-semibold brand-text">{doc.client_name}</div>
        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 shrink-0" data-testid={`portal-doc-state-${doc.key}`}>Recebido · em verificação</span>
      </div>
      <div className="mt-3 rounded-lg bg-sky-50 text-sky-900 text-[15px] leading-snug px-3.5 py-3" data-testid={`portal-doc-claimed-${doc.key}`}>
        Obrigado. Avisámos {consultant?.name ? <>o seu consultor, {consultant.name},</> : "o seu consultor"} para acrescentar <b>{doc.owner_name}</b> ao processo. Ele entra em contacto se precisar de mais alguma coisa.
      </div>
    </div>
  );
}

function UploadButtons({ doc, state, sideRef, inputRef, camRef, compact, bare }) {
  if (doc.two_sided) {
    return (
      <div className="mt-3 grid grid-cols-2 gap-2" data-testid={`portal-doc-sides-${doc.key}`}>
        {[["frente", "Frente"], ["verso", "Verso"]].map(([s, l]) => {
          const got = !!doc.sides?.[s];
          return (
            <button key={s} onClick={() => { sideRef.current = s; inputRef.current?.click(); }} disabled={state?.status === "reading"}
              data-testid={`portal-doc-side-${s}-${doc.key}`}
              className={`h-11 inline-flex items-center justify-center gap-2 rounded-lg font-semibold text-sm transition-opacity hover:opacity-90 disabled:opacity-60 ${got ? "border-2 border-green-200 bg-green-50 text-green-800" : "text-white"}`}
              style={got ? undefined : DARK}>
              {state?.status === "reading" && sideRef.current === s ? <Loader2 size={16} className="animate-spin" /> : got ? <CheckCircle2 size={16} /> : <Camera size={16} />} {l}
            </button>
          );
        })}
        <p className="col-span-2 text-xs text-slate-500" data-testid={`portal-doc-sides-hint-${doc.key}`}>Passaporte? Envie só a página com a fotografia, em «Frente».</p>
      </div>
    );
  }
  if (compact) {
    return (
      <button onClick={() => inputRef.current?.click()} disabled={state?.status === "reading"} data-testid={`portal-doc-send-${doc.key}`}
        className="mt-3 w-full h-12 inline-flex items-center justify-center gap-2 rounded-xl font-semibold text-[15px] text-white transition-opacity hover:opacity-90 disabled:opacity-60" style={DARK}>
        {state?.status === "reading" ? <Loader2 size={17} className="animate-spin" /> : <FileUp size={17} />} Enviar
      </button>
    );
  }
  return (
    <div className={`mt-3 grid gap-2 ${bare ? "grid-cols-1 gap-3" : "grid-cols-2"}`} data-testid={`portal-doc-btns-${doc.key}`}>
      {[["photo", camRef, Camera], ["file", inputRef, FileUp]].map(([k, ref, Icon]) => (
        <button key={k} onClick={() => ref.current?.click()} disabled={state?.status === "reading"} data-testid={`portal-doc-${k}-${doc.key}`}
          className={`${bare ? "h-14 rounded-xl text-[16px] text-white" : `h-11 rounded-lg text-sm ${k === "photo" ? "text-white" : "border-2 border-slate-200 brand-text"}`} inline-flex items-center justify-center gap-2 font-semibold transition-opacity hover:opacity-90 disabled:opacity-60`}
          style={bare || k === "photo" ? DARK : undefined}>
          {state?.status === "reading" ? <Loader2 size={16} className="animate-spin" /> : <Icon size={16} />} {T[k]}
        </button>
      ))}
    </div>
  );
}

// Cartão de um documento do cliente: o estado decide o que mostra (em falta, a confirmar, enviado).
export function DocumentStatusCard({ token, session, doc, consultant, isOpen, onToggleHelp, onUploaded, compact = false, why = null, ctx = "a compra e venda", bare = false }) {
  const inputRef = useRef();
  const camRef = useRef();
  const sideRef = useRef(null);
  const [state, setState] = useState(null); // {status, line}
  useEffect(() => { setState(null); }, [doc.todo, doc.state]);
  const done = state?.status === "done" && !doc.todo;

  const upload = async (e) => {
    const files = [...(e.target.files || [])];
    const file = files[0];
    e.target.value = ""; // permite escolher o mesmo ficheiro outra vez (ex.: «image.jpg» da câmara do iPhone)
    if (!file) return;
    setState({ status: "reading", line: null });
    try {
      const fd = await makeForm([["file", file], ...(doc.multi_page ? files.slice(1).map((f) => ["more", f]) : []), ["key", doc.key],
        ...(sideRef.current ? [["side", sideRef.current]] : [])], portalChunks(token, session));
      const { data } = await axios.post(`${API}/portal/${token}/upload`, fd, { headers: { "X-Portal-Session": session } });
      if (data.status === "rejeitado") {
        setState({ status: "error", line: data.line });
      } else {
        setState({ status: "done", line: data.line });
        setTimeout(onUploaded, 700);
      }
    } catch (ex) {
      setState({ status: "error", line: errOf(ex, "Não foi possível enviar. Tente novamente.") });
    }
  };

  const toggle = () => onToggleHelp(doc.key, true);
  if (doc.state === "titular") return <TitularCard token={token} session={session} doc={doc} onDone={onUploaded} />;
  if (doc.owner_claimed) return <ClaimedCard doc={doc} consultant={consultant} />;
  if (["a_verificar", "ok"].includes(doc.state)) return <SentCard token={token} session={session} doc={doc} onDone={onUploaded} />;

  return (
    <div className={bare ? "" : "bg-white rounded-xl border border-slate-100 p-4 transition-shadow"} data-testid={`portal-doc-${doc.key}`}>
      {!bare && <div className="flex items-start gap-2">
        <div className={`flex-1 min-w-0 font-semibold brand-text pt-2 ${compact ? "text-[16px]" : ""}`} data-testid={`portal-doc-title-${doc.key}`}>{compact ? sendTitle(doc) : doc.client_name}</div>
        {!isCard(doc.key) && <button onClick={toggle} aria-label="Como obtenho isto?" data-testid={`portal-help-toggle-${doc.key}`}
          className={`shrink-0 grid place-items-center w-10 h-10 -mr-2 rounded-full transition-colors ${isOpen ? "brand-text brand-soft" : "text-slate-400 brand-hover hover:bg-slate-50"}`}>
          <Info size={20} />
        </button>}
      </div>}
      {doc.todo && <div className="mt-2 text-sm rounded-lg bg-red-50 text-red-700 px-3 py-2" data-testid={`portal-doc-todo-${doc.key}`}>{doc.todo}</div>}
      {doc.where && !compact && !bare && <div className="mt-1 text-sm text-slate-500" data-testid={`portal-doc-where-${doc.key}`}>{doc.where}</div>}
      {compact && !doc.todo && <div className="mt-0.5 text-sm text-slate-500" data-testid={`portal-doc-why-${doc.key}`}>{sendWhy(doc, ctx, why)}</div>}
      {doc.consultant_handles && <div className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 rounded-lg bg-slate-100 px-3 py-1.5" data-testid={`portal-doc-handed-${doc.key}`}><Clock size={15} /> O consultor vai tratar</div>}
      {done ? (
        <div className="mt-3 inline-flex items-center gap-1.5 text-sm text-slate-600 font-medium" data-testid={`portal-doc-done-${doc.key}`}>
          <Clock size={16} /> {STATE_TXT.review}
        </div>
      ) : <UploadButtons doc={doc} state={state} sideRef={sideRef} inputRef={inputRef} camRef={camRef} compact={compact} bare={bare} />}
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={upload} data-testid={`portal-doc-camera-input-${doc.key}`} />
      <input ref={inputRef} type="file" accept=".pdf,image/*,.docx" {...(doc.multi_page ? { multiple: true } : {})} className="hidden"
        onChange={upload} data-testid={`portal-doc-input-${doc.key}`} />
      {doc.multi_page && !done && <p className="mt-1.5 text-xs text-slate-500 text-center" data-testid={`portal-doc-multi-${doc.key}`}>{compact ? "Várias páginas? Escolha as fotos todas de uma vez." : "Em papel com várias páginas? Escolha as fotografias todas de uma vez: juntamos num só PDF."}</p>}
      {state?.status === "error" && <div className="mt-2 text-xs text-red-600" data-testid={`portal-doc-error-${doc.key}`}>{state.line}</div>}
      {state?.status === "done" && state.line && <div className="mt-2 text-xs text-green-700" data-testid={`portal-doc-line-${doc.key}`}>{state.line}</div>}
      <div className={compact ? "mt-1.5 flex flex-wrap items-center justify-center gap-x-4" : bare ? "mt-4 space-y-1" : ""}>
      {!done && !isCard(doc.key) && (
        <button onClick={toggle} className={`${compact ? "" : "mt-2 block mx-auto "}text-sm text-slate-500 underline underline-offset-2 hover:text-slate-700 py-1`} data-testid={`portal-doc-howto-${doc.key}`}>
          {compact ? "Como obter?" : doc.noun ? `Como pedir a ${doc.noun}?` : "Como obter este documento?"}
        </button>
      )}
      {!done && !doc.todo && !doc.consultant_handles && (doc.later
        ? <p className="text-xs text-slate-500 text-center" data-testid={`portal-doc-later-note-${doc.key}`}>Fica para depois. Pode enviar quando tiver.</p>
        : <button onClick={() => axios.post(`${API}/portal/${token}/documents/${doc.key}/later`, {}, { headers: { "X-Portal-Session": session } }).then(onUploaded)}
            className={`${compact ? "" : "block mx-auto "}text-sm text-slate-500 hover:text-slate-700 py-1`} data-testid={`portal-doc-later-${doc.key}`}>Não tenho agora</button>)}
      </div>
    </div>
  );
}
